import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Evaluation, Participant } from '@/types/evaluation.types';
import { storageService } from '@/services/local-storage';
import { resendInvitation } from '@/services/participants/invitationService';
import { ParticipantList } from '@/components/participants/ParticipantList';
import { AddParticipantForm } from '@/components/participants/AddParticipantForm';
import { RemoveParticipantDialog } from '@/components/participants/RemoveParticipantDialog';
import { EditParticipantDialog } from '@/components/participants/EditParticipantDialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle, Check, Loader2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export function ParticipantManager() {
  const { t } = useTranslation(['participants', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState<Participant | null>(null);
  const [participantToEdit, setParticipantToEdit] = useState<Participant | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Load evaluation on mount
  useEffect(() => {
    if (!id) {
      navigate('/');
      return;
    }

    try {
      const loadedEvaluation = storageService.getEvaluation(id);
      if (!loadedEvaluation) {
        navigate('/');
        return;
      }
      setEvaluation(loadedEvaluation);
    } catch (error) {
      console.error('Failed to load evaluation:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Auto-save when participants change (following CriteriaBuilder pattern)
  useEffect(() => {
    if (evaluation && !loading) {
      setSaveStatus('saving');
      try {
        storageService.saveEvaluation({
          ...evaluation,
          modifiedDate: new Date(),
        });
        setSaveStatus('saved');
        // Reset to idle after 2 seconds
        const timeout = setTimeout(() => setSaveStatus('idle'), 2000);
        return () => clearTimeout(timeout);
      } catch (error) {
        console.error('Failed to save evaluation:', error);
        setSaveStatus('error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation?.participants, loading]);

  // Handle participant removal - show confirmation dialog
  const handleRemoveParticipant = (participantId: string) => {
    if (!evaluation) return;

    const participant = evaluation.participants.find((p) => p.id === participantId);
    if (participant) {
      setParticipantToRemove(participant);
    }
  };

  // Confirm participant removal
  const confirmRemoveParticipant = () => {
    if (!evaluation || !participantToRemove) return;

    const updatedParticipants = evaluation.participants.filter(
      (p) => p.id !== participantToRemove.id
    );

    const updatedEvaluation: Evaluation = {
      ...evaluation,
      participants: updatedParticipants,
    };

    setEvaluation(updatedEvaluation);
    setParticipantToRemove(null);
    // Auto-save will handle saving
  };

  // Handle showing the add participant form
  const handleAddClick = () => {
    setShowAddForm(true);
  };

  // Handle adding a new participant
  const handleAddParticipant = (newParticipant: Omit<Participant, 'id'>) => {
    if (!evaluation) return;

    // Create participant with ID
    const participantWithId: Participant = {
      ...newParticipant,
      id: storageService.generateId(),
    };

    // Add to evaluation
    const updatedEvaluation: Evaluation = {
      ...evaluation,
      participants: [...evaluation.participants, participantWithId],
    };

    setEvaluation(updatedEvaluation);
    setShowAddForm(false);
    // Auto-save will handle saving
  };

  // Handle canceling add form
  const handleCancelAdd = () => {
    setShowAddForm(false);
  };

  // Handle edit participant - show dialog
  const handleEditParticipant = (participantId: string) => {
    if (!evaluation) return;

    const participant = evaluation.participants.find((p) => p.id === participantId);
    if (participant) {
      setParticipantToEdit(participant);
    }
  };

  // Handle saving participant edits
  const handleSaveParticipantEdits = (participantId: string, updates: Partial<Participant>) => {
    if (!evaluation) return;

    const updatedParticipants = evaluation.participants.map((p) =>
      p.id === participantId ? { ...p, ...updates } : p
    );

    const updatedEvaluation: Evaluation = {
      ...evaluation,
      participants: updatedParticipants,
    };

    setEvaluation(updatedEvaluation);
    // Auto-save will handle saving
  };

  // Handle resend invitation
  const handleResendInvitation = (participantId: string) => {
    if (!evaluation) return;

    const updatedParticipants = evaluation.participants.map((p) =>
      p.id === participantId
        ? resendInvitation(p, evaluation.invitationExpirationDays)
        : p
    );

    const updatedEvaluation: Evaluation = {
      ...evaluation,
      participants: updatedParticipants,
    };

    setEvaluation(updatedEvaluation);
    // Auto-save will handle saving
  };

  // Show loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  // Show error if evaluation not found
  if (!evaluation) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('common:error')}</AlertTitle>
          <AlertDescription>
            {t('participants:errors.evaluationNotFound')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate(`/evaluation/${id}`)}
      >
        <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
        {t('common:navigation.backToDashboard')}
      </Button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">
              {t('participants:title')}
            </h1>
            <p className="text-muted-foreground">{t('participants:subtitle')}</p>
          </div>

          {/* Save Status Indicator */}
          {saveStatus !== 'idle' && (
            <div className="flex items-center gap-2 text-sm">
              {saveStatus === 'saving' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
                  <span className="text-muted-foreground">{t('participants:saveStatus.saving')}</span>
                </>
              )}
              {saveStatus === 'saved' && (
                <>
                  <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
                  <span className="text-green-600">{t('participants:saveStatus.saved')}</span>
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
                  <span className="text-destructive">{t('participants:saveStatus.error')}</span>
                </>
              )}
              <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {saveStatus === 'saving' && t('participants:saveStatus.saving')}
                {saveStatus === 'saved' && t('participants:saveStatus.saved')}
                {saveStatus === 'error' && t('participants:saveStatus.error')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Participant List */}
      <ParticipantList
        participants={evaluation.participants}
        onRemove={handleRemoveParticipant}
        onEdit={handleEditParticipant}
        onResendInvitation={handleResendInvitation}
        onAddClick={handleAddClick}
      />

      {/* Add Participant Form */}
      {showAddForm && (
        <div className="mt-8">
          <AddParticipantForm
            existingParticipants={evaluation.participants}
            criteriaGroups={evaluation.criteriaGroups}
            invitationExpirationDays={evaluation.invitationExpirationDays}
            onAdd={handleAddParticipant}
            onCancel={handleCancelAdd}
            generateId={storageService.generateId}
          />
        </div>
      )}

      {/* Edit Participant Dialog */}
      <EditParticipantDialog
        participant={participantToEdit}
        existingParticipants={evaluation.participants}
        criteriaGroups={evaluation.criteriaGroups}
        isOpen={!!participantToEdit}
        onClose={() => setParticipantToEdit(null)}
        onSave={handleSaveParticipantEdits}
      />

      {/* Remove Participant Confirmation Dialog */}
      <RemoveParticipantDialog
        participant={participantToRemove}
        isOpen={!!participantToRemove}
        onClose={() => setParticipantToRemove(null)}
        onConfirm={confirmRemoveParticipant}
      />
    </div>
  );
}
